import { KatDataTable } from "@amzn/katal-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import type { NestTestResult } from "src/api/interfaces/nest/history";
import { NestStatusBadge } from "../../NestStatusBadge";
import "./index.css";

export type SortDirection = "asc" | "desc";
export type SortField = "submissionDate" | "status" | "fileName";

export interface NestHistorySortState {
  field: SortField;
  direction: SortDirection;
}

const DEFAULT_SORT: NestHistorySortState = {
  field: "submissionDate",
  direction: "desc",
};

interface NestHistoryTableProps {
  data: NestTestResult[];
  pageIndex?: number;
  pageSize?: number;
  onRowClick?: (test: NestTestResult) => void;
  onSortChange?: (sort: NestHistorySortState) => void;
}

function compareTests(
  a: NestTestResult,
  b: NestTestResult,
  sort: NestHistorySortState
): number {
  const direction = sort.direction === "asc" ? 1 : -1;
  switch (sort.field) {
    case "submissionDate":
      return (
        direction *
        (new Date(a.submissionDate).getTime() -
          new Date(b.submissionDate).getTime())
      );
    case "status":
      return direction * a.status.localeCompare(b.status);
    case "fileName":
      return direction * a.fileName.localeCompare(b.fileName);
  }
}

export function NestHistoryTable({
  data,
  pageIndex,
  pageSize,
  onRowClick,
  onSortChange,
}: NestHistoryTableProps) {
  const { t } = useTranslation();
  const mountedElementsRef = useRef<Set<HTMLElement>>(new Set());
  const onRowClickRef = useRef(onRowClick);
  onRowClickRef.current = onRowClick;

  const [sort, setSort] = useState<NestHistorySortState>(DEFAULT_SORT);

  useEffect(() => {
    const elements = mountedElementsRef.current;
    return () => {
      elements.forEach((el) => {
        ReactDOM.unmountComponentAtNode(el);
      });
      elements.clear();
    };
  }, []);

  const columns = useMemo<KatDataTable.ColumnConfiguration[]>(() => {
    const createCellFormatter = (
      renderContent: (element: HTMLDivElement, rowData: any) => void
    ) => {
      return (_: any, rowData: any, lastElement: any) => {
        const element = lastElement || document.createElement("div");
        element.className = "nest-history-clickable-cell";
        element.onclick = () => onRowClickRef.current?.(rowData._original);
        renderContent(element, rowData);
        return { element };
      };
    };

    const createReactCellFormatter = (
      renderContent: (rowData: any) => React.ReactElement
    ) => {
      return (_: any, rowData: any, lastElement: any) => {
        const element = lastElement || document.createElement("div");
        element.className = "nest-history-clickable-cell";
        element.onclick = () => onRowClickRef.current?.(rowData._original);
        ReactDOM.render(renderContent(rowData), element);
        mountedElementsRef.current.add(element);
        return { element };
      };
    };

    return [
      {
        label: t("aic_nest_history_column_date"),
        property: "submissionDate",
        sortable: true,
        formatter: createCellFormatter((el, row) => {
          el.textContent = row.submissionDate;
        }),
      },
      {
        label: t("aic_nest_history_column_fileName"),
        property: "fileName",
        sortable: true,
        formatter: createCellFormatter((el, row) => {
          el.textContent = row.fileName;
        }),
      },
      {
        label: t("aic_nest_history_column_status"),
        property: "status",
        sortable: true,
        formatter: createReactCellFormatter((row) => (
          <NestStatusBadge status={row.status} />
        )),
      },
      {
        label: t("aic_nest_history_column_validations"),
        property: "validations",
        formatter: createCellFormatter((el, row) => {
          el.textContent = row.validations;
        }),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const sortedData = useMemo(
    () => [...data].sort((a, b) => compareTests(a, b, sort)),
    [data, sort]
  );

  const visibleData = useMemo(() => {
    if (!pageIndex || !pageSize) return sortedData;
    const start = (pageIndex - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, pageIndex, pageSize]);

  const rowData = visibleData.map((row) => ({
    submissionDate: new Date(row.submissionDate).toLocaleString(),
    fileName: row.fileName,
    status: row.status,
    validations: `${row.totalValidations - row.failedValidations}/${row.totalValidations}`,
    _original: row,
  }));

  const handleSortColumnChanged = (e: any) => {
    const { column, sortDirection } = e.detail ?? {};
    if (!column) return;

    const fieldMap: Record<string, SortField> = {
      submissionDate: "submissionDate",
      status: "status",
      fileName: "fileName",
    };
    const field = fieldMap[column.property];
    if (!field) return;

    const next: NestHistorySortState = { field, direction: sortDirection };
    setSort(next);
    onSortChange?.(next);
  };

  return (
    <div className="nest-history-table-container">
      <KatDataTable
        className="nest-history-table"
        columns={columns}
        rowData={rowData}
        onSortColumnChanged={handleSortColumnChanged}
      />
    </div>
  );
}
